import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier, XYCoord } from 'dnd-core';
import { ReactComponent as DragIcon } from '../../../../../../shared/icons/drag.svg';
import styles from './ApprovalOrder.module.scss';

interface ApprovalOrderItemProps {
  id: string;
  index: number;
  order: number;
  name: string;
  email: string;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  isDisabled?: boolean;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const ApprovalOrderItem: React.FC<ApprovalOrderItemProps> = ({
  id,
  index,
  order,
  name,
  email,
  moveItem,
  isDisabled = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: 'approver',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'approver',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isDisabled,
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`${styles.approverItem} ${isDragging ? styles.dragging : ''} ${isDisabled ? styles.disabled : ''}`}
    >
      <span className={styles.orderBadge}>{order}</span>
      {!isDisabled && (
        <span className={styles.dragHandle}>
          <DragIcon />
        </span>
      )}
      <div className={styles.approverInfo}>
        <div className={styles.name}>{name}</div>
        <div className={styles.email}>{email}</div>
      </div>
    </div>
  );
};

export default ApprovalOrderItem;
